/**
 * purchase (구매) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { createUrlWithQuery } from "@erp/shared/utils/https/create-url-with-query";
import { ORDER_APPROVAL_PHASE } from "@erp/shared/types/order-approval-phase.types";
import { PURCHASE_APPROVAL_PHASE } from "@erp/shared/types/purchase-approval-phase.types";
import { PRODUCT_TYPE } from "@erp/shared/types/product.types";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import { z } from "zod";
import { ProjectInfoSchema, UserInfoSchema } from "./default-schemes";
const PURCHASE_URLS = {
    commercial: {
        confirm: {
            list: "/purchase/confirm/commercial",
            setProjectCode: (id) => `/purchase/confirm/commercial/${id}/projectCode`,
            splitTempPurchase: (id) => `/purchase/confirm/commercial/${id}/divide`,
        },
        product: {
            list: "/purchase/product/commercial",
            editGroup: "/purchase/product/commercial",
            addFile: (id) => `/purchase/product/commercial/${id}/add-purchase-file`,
        },
    },
    research: {
        confirm: {
            list: "/purchase/confirm/research",
            setProjectCode: (id) => `/purchase/confirm/research/${id}/projectCode`,
            splitTempPurchase: (id) => `/purchase/confirm/research/${id}/divide`,
        },
        product: {
            list: "/purchase/product/research",
            editGroup: "/purchase/product/research",
            addFile: (id) => `/purchase/product/research/${id}/add-purchase-file`,
        },
    },
};
// ── 스키마 & 타입 (PurchaseConfirm) ──
export const GetPurchaseConfirmResponseSchema = z
    .object({
    id: z.coerce.number(), creater: z.coerce.number(),
    createrId: z.string(), createdTime: z.string(), createrName: z.string(),
    approvalPhase: z.nativeEnum(PURCHASE_APPROVAL_PHASE),
    approvedDate: z.string().nullable(), rejectedDate: z.string().nullable(),
    rejectReason: z.string().nullable(), note: z.string().nullable(),
    projectCode: z.string().nullable(), projectId: z.coerce.number().nullable(),
    purchaseManager: z.coerce.number().nullable(),
    purchaseManagerId: z.string().nullable(),
    purchaseManagerName: z.string().nullable(),
    obtainConfirmationId: z.coerce.number().nullable(),
    totalProducts: z.coerce.number(), requestedProducts: z.coerce.number(),
    _creater: UserInfoSchema, _purchaseManager: UserInfoSchema.nullable(),
    project: ProjectInfoSchema.nullable(),
})
    .strict();
// ── 스키마 & 타입 (PurchaseProduct) ──
export const GetPurchaseProductResponseSchema = z
    .object({
    id: z.coerce.number(), projectId: z.coerce.number().nullable(),
    purchaseId: z.coerce.number(), unitPrice: z.coerce.number(),
    purchaseNum: z.coerce.number(), purchaseEstimateFile: z.string().nullable(),
    orderId: z.coerce.number().nullable(), orderRequestDate: z.string().nullable(),
    groupName: z.string().nullable(), productId: z.coerce.number(),
    type: z.nativeEnum(PRODUCT_TYPE), productCode: z.string(),
    name: z.string(), model: z.string(), spec: z.string(),
    classificationId: z.coerce.number().nullable(),
    classification: z.string().nullable(),
    manufacturerId: z.coerce.number().nullable(),
    manufacturer: z.string().nullable(),
    orderApprovalPhase: z.nativeEnum(ORDER_APPROVAL_PHASE).nullable(),
})
    .strict();
// ── API 함수 ──
function createGetPurchaseConfirm(type) {
    return async (params) => {
        const data = await customInstance({
            url: PURCHASE_URLS[type].confirm.list, method: "GET", params: params ?? undefined,
        });
        return validateArrayWithSchema(GetPurchaseConfirmResponseSchema, data);
    };
}
export const getCommercialPurchaseConfirm = createGetPurchaseConfirm("commercial");
export const getResearchPurchaseConfirm = createGetPurchaseConfirm("research");
function createGetPurchaseProduct(type) {
    return async (params) => {
        const url = createUrlWithQuery(PURCHASE_URLS[type].product.list, params ?? {});
        const data = await customInstance({ url, method: "GET" });
        return validateArrayWithSchema(GetPurchaseProductResponseSchema, data);
    };
}
export const getCommercialPurchaseProduct = createGetPurchaseProduct("commercial");
export const getResearchPurchaseProduct = createGetPurchaseProduct("research");
function createPostPurchaseConfirm(type) {
    return async (body) => {
        return customInstance({
            url: PURCHASE_URLS[type].confirm.list, method: "POST",
            headers: { "Content-Type": "application/json" }, data: body,
        });
    };
}
export const postCommercialPurchaseConfirm = createPostPurchaseConfirm("commercial");
export const postResearchPurchaseConfirm = createPostPurchaseConfirm("research");
function createPatchPurchaseConfirmApprove(type) {
    return async (purchaseConfirmId, userId, isApproved, comments) => {
        return customInstance({
            url: PURCHASE_URLS[type].confirm.list + `/${purchaseConfirmId}/approval`,
            method: "PATCH", headers: { "Content-Type": "application/json" },
            data: { approval: isApproved, rejectReason: comments || "", modifier: userId },
        });
    };
}
export const patchCommercialPurchaseConfirmApprove = createPatchPurchaseConfirmApprove("commercial");
export const patchResearchPurchaseConfirmApprove = createPatchPurchaseConfirmApprove("research");
function createPatchPurchaseGroup(type) {
    return async (modifier, data) => {
        return customInstance({
            url: PURCHASE_URLS[type].product.editGroup, method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: { modifier, items: data },
        });
    };
}
export const patchCommercialPurchaseGroup = createPatchPurchaseGroup("commercial");
export const patchResearchPurchaseGroup = createPatchPurchaseGroup("research");
function createSetPurchaseProjectCode(type) {
    return async (id, projectId) => {
        return customInstance({
            url: PURCHASE_URLS[type].confirm.setProjectCode(id), method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: { projectId },
        });
    };
}
export const setCommercialPurchaseProjectCode = createSetPurchaseProjectCode("commercial");
export const setResearchPurchaseProjectCode = createSetPurchaseProjectCode("research");
function createPatchPurchaseFile(type) {
    return async (purchaseId, url) => {
        return customInstance({
            url: PURCHASE_URLS[type].product.addFile(purchaseId), method: "PATCH",
            headers: { "Content-Type": "application/json" },
            data: { purchaseEstimateFile: url },
        });
    };
}
export const patchCommercialPurchaseFile = createPatchPurchaseFile("commercial");
export const patchResearchPurchaseFile = createPatchPurchaseFile("research");
function createSplitTempPurchase(type) {
    return async (purchaseConfirmId, data) => {
        return customInstance({
            url: PURCHASE_URLS[type].confirm.splitTempPurchase(purchaseConfirmId),
            method: "PATCH", headers: { "Content-Type": "application/json" }, data,
        });
    };
}
export const splitCommercialTempPurchase = createSplitTempPurchase("commercial");
export const splitResearchTempPurchase = createSplitTempPurchase("research");
export async function getPurchaseConfirmDetail(id) {
    const data = await customInstance({
        url: `/purchase/confirm/commercial/${id}`, method: "GET",
    });
    return validateWithSchema(GetPurchaseConfirmResponseSchema, data);
}
// ── SDK 객체 ──
export const purchaseApi = {
    getCommercialPurchaseConfirm,
    getResearchPurchaseConfirm,
    getCommercialPurchaseProduct,
    getResearchPurchaseProduct,
    postCommercialPurchaseConfirm,
    postResearchPurchaseConfirm,
    patchCommercialPurchaseConfirmApprove,
    patchResearchPurchaseConfirmApprove,
    patchCommercialPurchaseGroup,
    patchResearchPurchaseGroup,
    setCommercialPurchaseProjectCode,
    setResearchPurchaseProjectCode,
    patchCommercialPurchaseFile,
    patchResearchPurchaseFile,
    splitCommercialTempPurchase,
    splitResearchTempPurchase,
    getPurchaseConfirmDetail,
};
